/**
 * Validador de RUT Chileno
 * 
 * Implementa el algoritmo de verificación del dígito verificador
 * utilizando módulo 11 según estándares chilenos.
 * 
 * @author Conectemos Chile
 */

// ============================================================================
// VALIDACIÓN DE RUT
// ============================================================================

/**
 * Valida si un RUT chileno es válido
 * 
 * @param rut - RUT a validar (con o sin formato)
 * @returns true si el RUT es válido
 * 
 * @example
 * validateRut('12345678-9'); // true o false
 * validateRut('12.345.678-9'); // true o false
 * validateRut('123456789'); // true o false
 */
export function validateRut(rut: string): boolean {
  if (!rut || typeof rut !== 'string') {
    return false;
  }

  // Limpiar RUT: remover puntos, espacios, guiones
  const cleanRut = rut.replace(/[^0-9kK]/g, '').toLowerCase();
  
  // Validar largo mínimo y máximo
  if (cleanRut.length < 8 || cleanRut.length > 9) {
    return false;
  }
  
  // Separar cuerpo y dígito verificador
  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1);
  
  // Validar que el cuerpo sean solo números
  if (!/^\d+$/.test(body)) {
    return false;
  }
  
  // Calcular dígito verificador esperado
  let sum = 0;
  let multiplier = 2;
  
  // Recorrer el cuerpo de derecha a izquierda
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  // Calcular dígito verificador
  const remainder = sum % 11;
  const calculatedDv = 11 - remainder;
  
  let expectedDv: string;
  if (calculatedDv === 11) {
    expectedDv = '0';
  } else if (calculatedDv === 10) {
    expectedDv = 'k';
  } else {
    expectedDv = calculatedDv.toString();
  }
  
  return dv === expectedDv;
}

/**
 * Formatea un RUT al formato estándar chileno
 * 
 * @param rut - RUT sin formato
 * @returns RUT formateado (XX.XXX.XXX-X)
 * 
 * @example
 * formatRut('123456789'); // '12.345.678-9'
 * formatRut('12345678k'); // '12.345.678-K'
 */
export function formatRut(rut: string): string {
  if (!rut || typeof rut !== 'string') {
    return '';
  }

  // Limpiar RUT
  const cleanRut = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (cleanRut.length < 2) {
    return cleanRut;
  }
  
  // Separar cuerpo y DV
  const body = cleanRut.slice(0, -1);
  const dv = cleanRut.slice(-1);
  
  // Formatear cuerpo con puntos
  const formattedBody = body.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${formattedBody}-${dv}`;
}

/**
 * Limpia un RUT removiendo formato
 * 
 * @param rut - RUT con formato
 * @returns RUT sin puntos ni guión, en mayúsculas
 * 
 * @example
 * cleanRut('12.345.678-9'); // '123456789'
 * cleanRut('12345678-k'); // '12345678K'
 */
export function cleanRut(rut: string): string {
  if (!rut || typeof rut !== 'string') {
    return '';
  }
  
  return rut.replace(/[^0-9kK]/g, '').toUpperCase();
}

/**
 * Obtiene el dígito verificador de un RUT
 * 
 * @param rut - RUT con o sin formato
 * @returns Dígito verificador en mayúsculas
 */
export function getDV(rut: string): string {
  const cleanedRut = cleanRut(rut);
  return cleanedRut.slice(-1);
}

/**
 * Obtiene el cuerpo del RUT sin dígito verificador
 * 
 * @param rut - RUT con o sin formato
 * @returns Cuerpo del RUT (solo números)
 */
export function getRutBody(rut: string): string {
  const clean = cleanRut(rut);
  return clean.slice(0, -1);
}

// ============================================================================
// VALIDACIONES ADICIONALES
// ============================================================================

/**
 * Valida que un RUT no esté en lista de RUTs inválidos conocidos
 * 
 * @param rut - RUT a validar
 * @returns true si no está en la lista negra
 */
export function validateNotBlacklisted(rut: string): boolean {
  const blacklist = [
    '11111111-1',
    '22222222-2',
    '33333333-3',
    '44444444-4',
    '55555555-5',
    '66666666-6',
    '77777777-7',
    '88888888-8',
    '99999999-9',
    '00000000-0'
  ];
  
  const formattedRut = formatRut(rut);
  return !blacklist.includes(formattedRut);
}

/**
 * Valida que el RUT tenga un formato mínimamente aceptable
 * 
 * @param rut - RUT a validar
 * @returns true si tiene formato válido
 */
export function validateFormat(rut: string): boolean {
  if (!rut || typeof rut !== 'string') {
    return false;
  }

  // Debe tener al menos 8 caracteres (7 números + 1 DV)
  const cleanedRut = cleanRut(rut);
  if (cleanedRut.length < 8) {
    return false;
  }

  // Los primeros caracteres deben ser números
  const body = cleanedRut.slice(0, -1);
  if (!/^\d+$/.test(body)) {
    return false;
  }

  // El último debe ser número o K
  const dv = cleanedRut.slice(-1);
  if (!/^[0-9K]$/.test(dv)) {
    return false;
  }

  return true;
}

/**
 * Validación completa de RUT
 * 
 * @param rut - RUT a validar
 * @returns Objeto con resultado de validación detallado
 */
export function validateRutComplete(rut: string): {
  isValid: boolean;
  formatted: string;
  clean: string;
  errors: string[];
} {
  const errors: string[] = [];
  let formatted = '';
  let clean = '';

  // Verificar que se proporcionó un RUT
  if (!rut || typeof rut !== 'string' || rut.trim() === '') {
    errors.push('Debe ingresar un RUT');
  } else {
    clean = cleanRut(rut);
    formatted = formatRut(rut);

    // Validar formato
    if (!validateFormat(rut)) {
      errors.push('Formato de RUT inválido');
    }

    // Validar largo
    if (clean.length < 8 || clean.length > 9) {
      errors.push('RUT debe tener entre 8 y 9 caracteres');
    }

    // Validar dígito verificador
    if (!validateRut(rut)) {
      errors.push('Dígito verificador incorrecto');
    }

    // Validar lista negra
    if (!validateNotBlacklisted(rut)) {
      errors.push('RUT no válido');
    }
  }

  return {
    isValid: errors.length === 0,
    formatted,
    clean,
    errors
  };
}

// ============================================================================
// HELPERS PARA FORMULARIOS
// ============================================================================

/**
 * Formatea RUT mientras el usuario escribe
 * Para usar en eventos onChange
 * 
 * @param value - Valor actual del input
 * @param previousValue - Valor anterior (para detectar borrado)
 * @returns Valor formateado para mostrar
 */
export function formatRutOnInput(value: string, previousValue?: string): string {
  // Si está borrando, no formatear para permitir edición libre
  if (previousValue && value.length < previousValue.length) {
    return value;
  }

  const clean = value.replace(/[^0-9kK]/g, '');
  
  // No formatear hasta tener al menos 2 caracteres
  if (clean.length < 2) {
    return clean.toUpperCase();
  }

  // Formatear si tiene suficientes caracteres
  if (clean.length >= 8) {
    return formatRut(clean);
  }

  return clean.toUpperCase();
}

/**
 * Genera un RUT aleatorio válido para testing
 * 
 * @returns RUT válido generado aleatoriamente
 */
export function generateRandomRut(): string {
  // Generar cuerpo aleatorio de 7-8 dígitos
  const bodyLength = Math.random() > 0.5 ? 8 : 7;
  let body = '';
  
  for (let i = 0; i < bodyLength; i++) {
    body += Math.floor(Math.random() * 10).toString();
  }

  // Calcular DV correcto
  let sum = 0;
  let multiplier = 2;
  
  for (let i = body.length - 1; i >= 0; i--) {
    sum += parseInt(body[i], 10) * multiplier;
    multiplier = multiplier === 7 ? 2 : multiplier + 1;
  }
  
  const remainder = sum % 11;
  const calculatedDv = 11 - remainder;
  
  let dv: string;
  if (calculatedDv === 11) {
    dv = '0';
  } else if (calculatedDv === 10) {
    dv = 'K';
  } else {
    dv = calculatedDv.toString();
  }

  return formatRut(body + dv);
}